import React, { useEffect, useState } from "react";
import { Button, Form } from "antd";
import { ReactSVG } from "react-svg";
import socketIOClient from "socket.io-client";
import orderBy from "lodash/orderBy";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import ListContainer from "../../modules/report/containers/List";
import ReportsFilterForm from "../Forms/Filters/Reports";
import withPage from "./withPage";
import { DATE_FORMAT_QUERY, PAGE_SIZE } from "../../constants";
import "./Reports.scss";
import RoutePlanCurrent from "../RoutePlan/RoutePlanCurrent";
import CustomMenu from "../CustomMenu";

import { doAddOrdersRoutePlan } from "../../modules/routePlan/stores/thunk";
import { getTranslation } from "../../utils/helper";

import { doGetAllLabels } from "../../modules/label/stores/thunk";
import ReportsTable from "../Table/Reports";
import { doGetCountOrdersDispatchedByCompany } from "../../modules/report/stores/thunk";
import ReportsOrdersByCompaniesTable from "../Table/ReportOrdersByCompanies";

const socketURI = process.env.REACT_APP_SOCKET_URI || "";
const socket = socketIOClient(socketURI);

const ReportsPage = ({ isFetching, orders, metadata, query, pushTo, message, doGetAllOrdersDispatched, user }) => {
  const setting = useSelector(state => state.setting.setting) || user.company;
  const roles = user.roles;
  const [showFilters, setShowFilters] = useState(false);
  const [defaultSelectedItems, setDefaultSelectedItems] = useState([]);
  const [selectedKeyMenu, setSelectedKeyMenu] = useState(query?.pageType || "1");

  const isAdmin = roles.includes("admin");

  const currentPage = Number(query.currentPage) || 1;
  const itemsPerPage = Number(query.itemsPerPage) || PAGE_SIZE;
  const startDate = query.startDate;
  const endDate = query.endDate;

  const pagination = {
    current: currentPage,
    pageSize: itemsPerPage,
    total: metadata.totalItems,
    showTotal: total =>
      `${total} ${selectedKeyMenu === "2" && isAdmin ? "Companies" : getTranslation(setting, "Orders", "Orders")}`,
  };

  const [modifying, setModifying] = useState(false);
  const [form] = Form.useForm();
  const [state, setState] = useState({
    visibleModal: false,
    type: "",
    items: [],
  });

  const { companies, countOrdersByCompany } = useSelector(state => state.report || {});

  const stateRoutePlan = useSelector(state => state.routePlan);
  const messageRoutePlan = stateRoutePlan.message;
  const isFetchingRoutePlan = stateRoutePlan.isFetching;
  const dispatch = useDispatch();

  const routePlanType = query?.routePlanType || setting?.routePlanType || "PDP";

  useEffect(() => {
    let isSubscribed = true;
    if (isSubscribed) {
      dispatch(doGetAllLabels({}));
    }
    return () => {
      isSubscribed = false;
    };
  }, []);

  useEffect(() => {
    let isSubscribed = true;
    if (isSubscribed) {
      setShowFilters(false);
      pushTo({ query: undefined });
      if (selectedKeyMenu === "2" && isAdmin) {
        dispatch(
          doGetCountOrdersDispatchedByCompany({
            currentPage,
            itemsPerPage,
            ...query,
          }),
        );
      } else {
        doGetAllOrdersDispatched({
          currentPage,
          itemsPerPage,
          ...query,
        });
      }
    }
    return () => {
      isSubscribed = false;
    };
  }, [selectedKeyMenu, query?.routePlanType]);

  useEffect(() => {
    let isSubscribed = true;
    socket.connect();
    socket.on(user.company?._id, data => {
      if (data.event === "orders.geocoding.completed" && isSubscribed) {
        doGetAllOrdersDispatched({
          currentPage,
          itemsPerPage,
          ...query,
        });
      }
    });
    return () => {
      isSubscribed = false;
      socket.disconnect();
    };
  }, []);

  const handleFilter = (params, updatePath) => {
    if (isAdmin) {
      params = {
        ...params,
        pageType: selectedKeyMenu,
      };
    } else {
      params = {
        ...params,
        pageType: undefined,
      };
    }
    if (updatePath) {
      pushTo({ query: params });
    }
    const currentPage = Number(params?.currentPage) || 1;
    const itemsPerPage = Number(params?.itemsPerPage) || PAGE_SIZE;
    if (selectedKeyMenu === "2" && isAdmin) {
      dispatch(
        doGetCountOrdersDispatchedByCompany({
          currentPage,
          itemsPerPage,
          ...params,
        }),
      );
    } else {
      doGetAllOrdersDispatched({
        currentPage,
        itemsPerPage,
        ...params,
      });
    }
  };

  const handleTableChange = params => {
    setDefaultSelectedItems([]);
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  useEffect(() => {
    if (metadata?.allOrders?.length > 0) {
      setDefaultSelectedItems(metadata.allOrders);
      setState({ ...state, items: metadata.allOrders });
    }
  }, [metadata]);

  const handleCancel = () => {
    setState({ ...state, visibleModal: false });
    form.resetFields();
  };

  useEffect(() => {
    if (!isFetching && ["bulk_delete_succeeded", "bulk_update_succeeded"].includes(message)) {
      handleCancel();
      setTimeout(() => {
        if (message === "bulk_update_succeeded") {
          doGetAllOrdersDispatched({ currentPage, itemsPerPage, ...query });
        }
      }, 2000);
    }
  }, [isFetching, message]);

  useEffect(() => {
    if (!isFetchingRoutePlan && "add_orders_to_routeplan_succeeded" === messageRoutePlan) {
      handleCancel();
      setTimeout(() => {
        if (messageRoutePlan === "add_orders_to_routeplan_succeeded") {
          doGetAllOrdersDispatched({ currentPage, itemsPerPage, ...query });
        }
      }, 2000);
    }
  }, [isFetchingRoutePlan, messageRoutePlan]);

  useEffect(() => {
    if (!isFetchingRoutePlan && messageRoutePlan?.includes("create_routeplan_succeeded")) {
      dispatch(
        doAddOrdersRoutePlan({
          id: messageRoutePlan?.split("_")[3],
          orders: state.items.map(s => s._id),
        }),
      );
      setModifying(false);
    }
  }, [isFetchingRoutePlan, messageRoutePlan]);

  return (
    <div className="Content ReportsPage">
      {isAdmin && (
        <div className="header-menu-page">
          <CustomMenu className="ReportsMenu" selectedKeys={[isAdmin ? selectedKeyMenu : "1"]} mode="horizontal">
            <CustomMenu.Item key="1" onClick={() => setSelectedKeyMenu("1")}>
              <div className="ReportsMenuItem" onClick={() => setSelectedKeyMenu("1")}>
                Transaction
              </div>
            </CustomMenu.Item>
            <CustomMenu.Item key="2" onClick={() => setSelectedKeyMenu("2")}>
              <div className="ReportsMenuItem" onClick={() => setSelectedKeyMenu("2")}>
                Volume
              </div>
            </CustomMenu.Item>
          </CustomMenu>
        </div>
      )}
      <RoutePlanCurrent />
      <div className="ContentHeader">
        <div className="HeaderTitle">Dispatched orders {selectedKeyMenu === "2" && "by company"}</div>
        <div className="HeaderToolBar">
          <div className="ToolBarButtons">
            <Button
              className={`FilterButton ${showFilters ? "Open" : "Close"}`}
              type="primary"
              onClick={() => setShowFilters(!showFilters)}>
              {getTranslation(setting, "Common", "Filter")}
              <ReactSVG className="icon" src={`/icons/filter${showFilters ? "-close" : ""}.svg`} />
            </Button>
          </div>
        </div>
      </div>
      <div className="ContentFilters">
        {showFilters && (
          <ReportsFilterForm
            isFetching={isFetching}
            query={query}
            onSubmit={handleFilter}
            isOther={isAdmin && selectedKeyMenu === "2"}
          />
        )}
      </div>
      <div className="ContentMain">
        {!isAdmin || selectedKeyMenu === "1" ? (
          <>
            <div className="HeaderTableReport">
              {!!startDate && !!endDate && (
                <p>
                  For the period {moment(startDate, DATE_FORMAT_QUERY).format("DD MMMM YYYY")} to{" "}
                  {moment(endDate, DATE_FORMAT_QUERY).format("DD MMMM YYYY")}: <b>{metadata.totalItems}</b> dispatched
                  orders
                </p>
              )}
              {!!startDate && !endDate && (
                <p>
                  For the period from {moment(startDate, DATE_FORMAT_QUERY).format("DD MMMM YYYY")} :{" "}
                  <b>{metadata.totalItems}</b> dispatched orders
                </p>
              )}
              {!!endDate && !startDate && (
                <p>
                  For the period up to {moment(endDate, DATE_FORMAT_QUERY).format("DD MMMM YYYY")}:{" "}
                  <b>{metadata.totalItems}</b> dispatched orders
                </p>
              )}
              {!startDate && !endDate && (
                <p>
                  <b>{metadata.totalItems}</b> dispatched orders
                </p>
              )}
            </div>
            <ReportsTable
              rowKey="_id"
              roles={roles}
              loading={isFetching}
              query={query}
              pagination={pagination}
              dataSource={orders}
              onChange2={handleTableChange}
              scrollHeight="calc(100vh - 350px)"
              actionWidth={190}
              modifying={modifying}
              defaultSelectedItems={defaultSelectedItems}
              setDefaultSelectedItems={setDefaultSelectedItems}
              setting={setting}
              routePlanType={routePlanType}
            />
          </>
        ) : (
          <ReportsOrdersByCompaniesTable
            rowKey="_id"
            roles={roles}
            loading={isFetching}
            query={query}
            pagination={false}
            dataSource={orderBy(companies, c => countOrdersByCompany[c._id] || 0, "desc")}
            ordersByCompany={countOrdersByCompany}
            scrollHeight="calc(100vh - 350px)"
          />
        )}
      </div>
    </div>
  );
};

export default withPage(ListContainer(ReportsPage));
